import React, { useContext, useState } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { Photo, ThumbnailSelectionMode } from "./types";
import { Photo as MasterImageSearchPhoto } from "../MasterImageSearch/components/types";
import LightBox from "./LightBox";
import { EventGalleryContext } from "./index";
import { FindPhotosGalleryContext } from "../MasterImageSearch/components/results/PhotosContainer";
import { ReactWindowPhotoGrid } from "./PhotoGrid";
import Telemetry from "../../utils/rudderstackAnalytics";
import ahoy from "ahoy.js";
import { ThumbnailWidth } from "./ImageThumbnail2";
import { gssErrorLog } from "../../utils/gssLogger";
import "./styles/photos2.css";

interface PhotosProps {
  photos: Photo[];
  windowingProps: {
    hasMore: boolean;
    isLoading: boolean;
    loadMore: () => void;
  };
  selectionMode: ThumbnailSelectionMode;
  isMobile: boolean;
  event: any;
  album: any; // Optional
  trackTelemetry: boolean;
  userId: number | undefined;
  isAuthorizedForDirectDownloads: boolean;
  primaryColor?: string;
  parentName: string;
  showFavIcon: boolean;
  canShowGuestEmailModal: (photo: any) => boolean;
  galleryPhotos: (photos: Photo[]) => Photo[];
  securedOrgView: boolean | null;
  registerGuestUserAPI: string;
  setGuestUserId: (userId: number) => void;
  eventClickable?: boolean;
  disableSelectIcon?: boolean;
  secureAccessToken?: string;
}

export default function Photos2(props: PhotosProps) {
  const forceRender = false;
  const selectionMode = props.selectionMode;
  const isMobile = props.isMobile;
  const [photoIndex, setPhotoIndex] = useState(-1);
  const [curationApiCalled, setCurationApiCalled] = useState(false);
  const [currentMedia, setCurrentMedia] = useState<{
    mediaId: number;
    currentState: boolean;
  }>();
  const context = useContext(EventGalleryContext);
  const findPhotosContext = useContext(FindPhotosGalleryContext);

  const photos = props.photos;
  const trackTelemetry = props.trackTelemetry;
  const isAuthorizedForDirectDownloads = props.isAuthorizedForDirectDownloads;
  const userId = props.userId;
  const primaryColor = props.primaryColor;
  const parentName = props.parentName;
  const showFavIcon = props.showFavIcon;

  const onClickThumbnail = (photo: Photo) => {
    const index = photos.findIndex((p: Photo) => p.id === photo.id);
    if (index === -1) {
      console.error("Photos2: onClickThumbnail: photo not found", photo.id);
      return;
    }
    toggleLightBox(index);
  };

  const toggleLightBox = (index: number) => {
    try {
      toggleLightBox1(index);
    } catch (error) {
      gssErrorLog("Photos2: toggleLightBox: error", error);
      setPhotoIndex(-1);
    }
  };

  const toggleLightBox1 = (index: number) => {
    if (photos[photoIndex] && photos[photoIndex].videoContent) {
      setPhotoIndex(-1);
    }
    setPhotoIndex(index);
    if (index == -1) {
      setPhotoIndex(-1);
    }
    if (index >= 0) {
      if (!photos[index]) {
        // Unusual but possible
        gssErrorLog(
          "Photos2: toggleLightBox: photos[index] is undefined",
          index
        );
        return;
      }
      if (!context.guestUserId) {
        setCurrentMedia({
          mediaId: photos[index].id,
          currentState: photos[index].favorite
        });
      }
      if (trackTelemetry) {
        ahoy.track("albumInteraction", {
          secondary_type: "openedPhotoLightbox",
          album_id: photos[index].album.id,
          event_id: photos[index].album.eventId
        });
        Telemetry.track("event-gallery:opened-lightbox", {
          eventId: context.event.id,
          eventName: context.event.name,
          photoId: photos[index].id,
          embeddedMode: context.isEmbed
        });
      }
    }
  };

  const updatePhotoInCart = (photo: Photo) => {
    if (photo.isSelected) {
      if (trackTelemetry) {
        Telemetry.track("event-gallery:remove-from-cart", {
          eventId: context.event?.id,
          eventName: context.event?.name,
          photoId: photo.id,
          cartId: context.cart?.id,
          source: "thumbnail",
          embeddedMode: context.isEmbed
        });
      }
      if (context.cart) context.removePhotoFromCart(photo);
      else {
        const photoMis = photo as unknown as MasterImageSearchPhoto;
        findPhotosContext.removePhotoFromCart(photoMis);
      }
    } else {
      if (trackTelemetry) {
        Telemetry.track("event-gallery:add-to-cart", {
          eventId: context.event?.id,
          eventName: context.event?.name,
          photoId: photo.id,
          cartId: context.cart?.id,
          source: "thumbnail",
          embeddedMode: context.isEmbed
        });
      }
      if (context.canShowEmailPopup()) {
        props.canShowGuestEmailModal(photo);
      } else if (context.event) {
        context.addPhotoToCart(photo);
      } else {
        const photoMis = photo as unknown as MasterImageSearchPhoto;
        findPhotosContext.addPhotoToCart(photoMis);
      }
    }
  };

  const onThumbnailSelect = (photo: Photo): void => {
    if (selectionMode === ThumbnailSelectionMode.CART) {
      updatePhotoInCart(photo);
    } else if (selectionMode === ThumbnailSelectionMode.COVER) {
      // TODO: Implement cover mode
    } else if (selectionMode === ThumbnailSelectionMode.CURATE) {
      // TODO: Implement curate mode
    }
  };

  // This is how we detect when the photo-grid is at the bottom of the screen
  // The header scrolls into view when the grid reaches the bottom
  // Handling Embed mode:
  // In embed mode, the page is often narrower because they add margins on the iframe
  // Which makes the grid too small (two columns only). Visible in screens < 390px
  // To make more room, we widen the grid by adding a little negative margin
  // ANd also reduce the size of the thumbnails
  // which covers up the the padding introduced by the .container
  const tooNarrow = window.innerWidth < 390;
  const widenedClass =
    window.gss?.isEmbedded || tooNarrow
      ? "widened-grid-with-negative-margin"
      : "";
  const thumbnailWidth =
    window.gss?.isEmbedded || tooNarrow ? ThumbnailWidth - 10 : ThumbnailWidth;

  const areMoreItemsLoading =
    props.windowingProps.isLoading ||
    (props.windowingProps.hasMore && photos.length % 100 === 0);

  return (
    <div
      className={`photos2-component ${widenedClass}`}
      style={{ overflowX: "hidden" }} // To avoid showing horizontal scroll
    >
      <AutoSizer disableHeight onResize={() => {}}>
        {({ width }: { width: number }) => (
          <ReactWindowPhotoGrid
            items={photos} //items: list of items to render
            hasMoreItems={props.windowingProps.hasMore}
            areMoreItemsLoading={areMoreItemsLoading}
            loadMoreItems={props.windowingProps.loadMore}
            numColumns={Math.floor(width / thumbnailWidth)}
            width={width}
            otherProps={{
              event: props.event,
              forceRender: forceRender,
              isMobile: isMobile || false,
              thumbnailProps: {
                onClick: onClickThumbnail,
                onThumbnailSelect: onThumbnailSelect,
                selectionMode: selectionMode,
                primaryColor: primaryColor,
                securedOrgView: props.securedOrgView,
                disableSelectIcon: props.disableSelectIcon
              },
              onScrollDownStarted: () => {}
            }}
          />
        )}
      </AutoSizer>
      {photos[photoIndex] && (
        <div className="dv-album-wrap">
          <LightBox
            photos={props.photos}
            photoIndex={photoIndex}
            toggleLightBox={toggleLightBox}
            trackTelemetry={trackTelemetry}
            isAuthorizedForDirectDownloads={isAuthorizedForDirectDownloads}
            userId={userId}
            primaryColor={primaryColor}
            curationApiCalled={curationApiCalled}
            setCurationApiCalled={(val: boolean) => setCurationApiCalled(val)}
            parentName={parentName}
            showFavIcon={showFavIcon}
            canShowGuestEmailModal={props.canShowGuestEmailModal}
            windowingProps={props.windowingProps}
            registerGuestUserAPI={props.registerGuestUserAPI}
            setGuestUserId={props.setGuestUserId}
            secureAccessToken={props.secureAccessToken}
            securedOrgView={props.securedOrgView ?? false}
          />
        </div>
      )}
    </div>
  );
}
